import React, { useState, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";
import { jwtDecode } from 'jwt-decode';
import { useLocation, useNavigate } from "react-router-dom";
import { Table, Space, Popover, Avatar, Select, Form, Switch, InputNumber, message, Layout } from "antd";
import { Helmet as HelmetAsync } from "react-helmet-async";
import styled from "styled-components";
import axios from '../../utils/axios';
import { useIntl } from "react-intl";
import {
  useAppDispatch,
  useAppSelector
} from "../../redux/store";
import {
  actionInteractionVotesSelector,
  searchFieldValueSelector,
  batchUpdateOffersSelector,
  remainingBudgetCurrentPeriodSelector
} from "../../redux/selectors";
import Modal from "../../components/Modal";
import Config from '../../config';
import {
  getActionInteraction,
  toggleSpinner,
  sendSelectedOffers,
  batchUpdateOffers,
  getRemainingBudgetCurrentPeriod
} from "../../redux/actions";
import { formatDate, getLocalStorageItem } from "../../utils/utilities";
import Tag from "../../components/Tag";
import StyledText from '../../components/StyledText';
import Button from "../../components/Button";
import pen from "../../assets/pen.svg";

const STORAGE_TYPE = 'sessionStorage';
const STORAGE_KEY = 'savedOffers';

const PreApprovedAction = ({ className }) => {
  const intl = useIntl();
  const baseClassName = clsx("applicationAction", className);
  const [isLoading, setIsLoading] = useState(false);
  const [sortState, setSortState] = useState({});
  const [allInteractions, setAllInteractions] = useState(null);
  const [filteredInteractions, setFilteredInteractions] = useState(null);
  const [remainingBudgetWithOffersValue, setRemainingBudgetWithOffersValue] = useState(0);
  const [savedOffers, setSavedOffers] = useState([]);
  const [roundOptions, setRoundOptions] = useState([]);
  const [roundId, setRoundId] = useState(null);
  const [form] = Form.useForm();
  const [offerMode, setOfferMode] = useState(false);
  const [showOfferConfirmationModal, setShowOfferConfirmationModal] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const votes = useAppSelector(actionInteractionVotesSelector);
  const batchUpdateOffersData = useAppSelector(batchUpdateOffersSelector);
  const searchFieldValue = useAppSelector(searchFieldValueSelector);
  const remainingBudgetCurrentPeriodData = useAppSelector(remainingBudgetCurrentPeriodSelector);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [offerValues, setOfferValues] = useState({});
  const decodeToken = (getLocalStorageItem('id_Token') && jwtDecode(getLocalStorageItem('id_Token'))) || {};
  const savedProgramId = sessionStorage.getItem('savedProgramId');
  const checkLocation = location.pathname === '/company/applications/pre-approved';
  const { remainingBudgetCurrentPeriod } = remainingBudgetCurrentPeriodData?.[0] || {};
  const currencySymbol = filteredInteractions?.[0]?.programOfferings?.currencySymbol || '£';

  const getStorage = () => (STORAGE_TYPE === 'localStorage' ? localStorage : sessionStorage);

  const loadSavedOffers = useCallback(() => {
    const storedOffers = getStorage().getItem(STORAGE_KEY);
    return storedOffers ? JSON.parse(storedOffers) : [];
  }, []);

  const saveSavedOffers = useCallback((offers) => {
    getStorage().setItem(STORAGE_KEY, JSON.stringify(offers));
  }, []);

  useEffect(() => {
    setSavedOffers(loadSavedOffers());
  }, [loadSavedOffers]);

  useEffect(() => {
    saveSavedOffers(savedOffers);
  }, [savedOffers, saveSavedOffers]);

  useEffect(() => {
    const handleStorageCleanup = () => {
      if (STORAGE_TYPE === 'localStorage' && !getLocalStorageItem('id_Token')) {
        localStorage.removeItem(STORAGE_KEY);
      }
    };
    window.addEventListener('storage', handleStorageCleanup);
    return () => window.removeEventListener('storage', handleStorageCleanup);
  }, []);

  const getAllInteractions = useCallback(async () => {
    if (!checkLocation) return;
    setIsLoading(true);
    try {
      if (Array.isArray(votes)) {
        setAllInteractions(votes);
        setFilteredInteractions(votes);
      } else {
        setAllInteractions([]);
        setFilteredInteractions([]);
      }
    } catch (error) {
      console.error('Error in getAllInteractions:', error);
      setAllInteractions([]);
      setFilteredInteractions([]);
    } finally {
      setIsLoading(false);
    }
  }, [checkLocation, votes]);

  useEffect(() => {
    if (checkLocation) {
      setIsLoading(true);
      setAllInteractions(null);
      setFilteredInteractions(null);
      dispatch(getActionInteraction({
        sortBy: "programName",
        asc: "true",
        filterBy: {
          status: ["PreApproved"],
          ...(savedProgramId !== "All" && savedProgramId && { programID: savedProgramId, companyID: decodeToken?.companyID }),
          ...(roundId && { currentBudgetEndDate: roundId })
        },
        limit: "100"
      }));
    } else {
      setAllInteractions([]);
      setFilteredInteractions([]);
      setSelectedRowKeys([]);
      setOfferValues({});
    }
  }, [checkLocation, dispatch, decodeToken?.companyID, savedProgramId, roundId, batchUpdateOffersData]);

  useEffect(() => {
    if (filteredInteractions && Array.isArray(filteredInteractions)) {
      const restoredOfferValues = {};
      filteredInteractions.forEach((record, idx) => {
        const key = idx;
        const savedOffer = savedOffers.find(offer => offer.programID === record.programID && offer.opportunityID === record.opportunityID);
        restoredOfferValues[key] = savedOffer?.offer || record.offer || record.totalCashRequested;
      });
      setOfferValues(restoredOfferValues);
    }
  }, [filteredInteractions, savedOffers]);

  useEffect(() => {
    if (votes !== undefined) {
      getAllInteractions();
    }
  }, [votes, getAllInteractions]);

  useEffect(() => {
    if (searchFieldValue && Array.isArray(allInteractions)) {
      const filterTable = allInteractions.filter((o) =>
        Object.values(o).some((value) =>
          String(value).toLowerCase().includes(searchFieldValue.toLowerCase())
        )
      );
      setFilteredInteractions(filterTable.length ? filterTable : []);
    } else if (!searchFieldValue && Array.isArray(allInteractions)) {
      setFilteredInteractions(allInteractions);
    }
  }, [searchFieldValue, allInteractions]);

  useEffect(() => {
    if (savedProgramId && savedProgramId !== "All") {
      dispatch(getRemainingBudgetCurrentPeriod({
        programID: savedProgramId,
        currentBudgetEndDate: "Latest"
      }));
    }
  }, [savedProgramId, dispatch]);

  useEffect(() => {
    if (savedProgramId && savedProgramId !== "All") {
      dispatch(toggleSpinner(true));
      axios.put(
        Config.url.getInteraction,
        (responseData) => {
          const rounds = responseData?.data?.uniqueProgrammeRound || [];
          const sortedRounds = rounds.sort((a, b) => new Date(b) - new Date(a));
          setRoundOptions(sortedRounds);
          if (sortedRounds.length > 0) {
            const latestRound = sortedRounds[0];
            setTimeout(() => {
              form.setFieldsValue({ donationRound: latestRound });
              setRoundId(latestRound);
              sessionStorage.setItem('savedRoundId', latestRound);
            }, 0);
          } else {
            form.setFieldsValue({ donationRound: undefined });
            setRoundId(null);
          }
          dispatch(toggleSpinner(false));
        },
        {
          sortBy: "programName",
          asc: "true",
          action: "uniqueProgrammeRound",
          filterBy: { programID: savedProgramId }
        },
        true
      );
    } else {
      setRoundOptions([]);
      setRoundId(null);
    }
  }, [savedProgramId, dispatch, form]);

  const totalCashRequested = useMemo(() => {
    return filteredInteractions?.reduce((sum, item) => {
      const cashValue = typeof item.totalCashRequested === 'string' && item.totalCashRequested != null
        ? parseFloat(item.totalCashRequested.trim()) || 0
        : parseFloat(item.totalCashRequested) || 0;
      return sum + cashValue;
    }, 0) || 0;
  }, [filteredInteractions]);

  const remainingBudgetWithOffersValueCalc = useMemo(() => {
    if (!filteredInteractions || !Array.isArray(filteredInteractions)) return 0;
    const offerMap = savedOffers.reduce((map, offer) => {
      const key = `${offer.programID}:${offer.opportunityID}`;
      map[key] = parseFloat(offer.offer) || 0;
      return map;
    }, {});

    const totalOffers = filteredInteractions.reduce((sum, record) => {
      const key = `${record.programID}:${record.opportunityID}`;
      const offerValue = offerMap[key] !== undefined
        ? offerMap[key]
        : (typeof record.offer === 'string' && record.offer != null ? parseFloat(record.offer) : parseFloat(record.offer) || parseFloat(record.totalCashRequested) || 0);
      return sum + (parseFloat(offerValue) || 0);
    }, 0);

    const totalCashWithoutOffer = filteredInteractions.reduce((sum, record) => {
      const key = `${record.programID}:${record.opportunityID}`;
      const hasOffer = offerMap[key] !== undefined || record.offer;
      return hasOffer ? sum : sum + (typeof record.totalCashRequested === 'string' && record.totalCashRequested != null ? parseFloat(record.totalCashRequested.trim()) : parseFloat(record.totalCashRequested) || 0);
    }, 0);

    const result = (parseFloat(remainingBudgetCurrentPeriod) || 0) - totalOffers + totalCashWithoutOffer;
    return result.toFixed(2);
  }, [filteredInteractions, savedOffers, remainingBudgetCurrentPeriod]);

  useEffect(() => {
    setRemainingBudgetWithOffersValue(remainingBudgetWithOffersValueCalc);
  }, [remainingBudgetWithOffersValueCalc]);

  const handleSort = useCallback((sortId) => {
    if (['programName', 'charityName', 'opportunityName', 'updateDate'].includes(sortId.key)) {
      const currentSortState = sortState[sortId.key] || 'asc';
      const newSortState = currentSortState === 'asc' ? 'desc' : 'asc';
      setSortState({ ...sortState, [sortId.key]: newSortState });
      setIsLoading(true);
      setAllInteractions(null);
      setFilteredInteractions(null);
      dispatch(getActionInteraction({
        sortBy: sortId.key,
        asc: newSortState === 'asc' ? "true" : "false",
        filterBy: {
          status: ["PreApproved"],
          ...(savedProgramId && savedProgramId !== "All" && {
            programID: savedProgramId,
            companyID: decodeToken?.companyID,
            ...(roundId && { currentBudgetEndDate: roundId })
          })
        },
        limit: "100"
      }));
    }
  }, [sortState, dispatch, savedProgramId, roundId, decodeToken?.companyID]);

  const handleOfferChange = useCallback((value, record) => {
    setOfferValues(prev => ({ ...prev, [record.key]: value }));
  }, []);

  const handleSendOffers = useCallback(() => {
    const hasEmptyOffers = selectedRowKeys.some(key => {
      const offerValue = offerValues[key];
      return offerValue === undefined || offerValue === null || offerValue === '';
    });
    if (hasEmptyOffers) {
      message.error(intl.formatMessage({ id: "please_fill_all_offers" }));
      return;
    }

    const messages = selectedRowKeys.map(key => {
      const record = filteredInteractions[key];
      return {
        programID: record.programID,
        opportunityID: record.opportunityID,
        message: intl.formatMessage(
          { id: "congratulations_we_would_like_to_approve_your_project" }
        ).replace('[currencysymbol]XXXX', `${currencySymbol}${parseFloat(offerValues[key]).toFixed(2)}`),
        messageStatus: "Offer Made and Editing Enabled"
      };
    });

    dispatch(sendSelectedOffers({
      action: "batchMessages",
      messages
    }));

    selectedRowKeys.forEach(key => {
      const record = filteredInteractions[key];
      axios.put(Config.url.addInteraction, (response) => {
        console.log(`Interaction ${record.opportunityID} set to edit mode`, response);
      }, {
        programID: record.programID,
        opportunityID: record.opportunityID,
        action: "EditMode",
        offer: offerValues[key].toString(),
      }, true);
    });

    setSelectedRowKeys([]);
    message.success(intl.formatMessage({ id: "offers_sent_successfully" }));
  }, [selectedRowKeys, offerValues, filteredInteractions, dispatch, intl, currencySymbol]);

  const handleSaveOffers = useCallback(() => {
    const offers = selectedRowKeys.map(key => {
      const record = filteredInteractions[key];
      const offerValue = offerValues[key] !== undefined ? offerValues[key] : (record.offer || record.totalCashRequested);

      if (offerValue === undefined || offerValue === null || offerValue === '') {
        message.error(intl.formatMessage({ id: "please_fill_all_offers" }));
        return null;
      }

      return {
        programID: record.programID,
        opportunityID: record.opportunityID,
        offer: offerValue.toString()
      };
    }).filter(offer => offer !== null);

    if (offers.length !== selectedRowKeys.length) {
      return;
    }

    setSavedOffers(offers);
    dispatch(batchUpdateOffers({ action: "BatchUpdateOffers", offers }));
    message.success(intl.formatMessage({ id: "offers_saved_successfully" }));
  }, [selectedRowKeys, offerValues, filteredInteractions, dispatch, intl]);

  const getTableData = useCallback((list) => {
    return Array.isArray(list) ? list.map((item, idx) => ({
      key: idx,
      programName: item.programName,
      charityName: item.charityName,
      opportunityName: item.opportunityName,
      updateDate: formatDate(item.updateDate),
      status: item.status,
      userID: item.userID,
      ID: item.ID,
      votes: item.votes,
      programID: item.programID,
      opportunityID: item.opportunityID,
      totalCashRequested: item.totalCashRequested,
      offer: offerValues[idx] !== undefined ? offerValues[idx] : (item.offer || item.totalCashRequested),
      prevStatus: item.prevStatus
    })) : [];
  }, [offerValues]);

  const getColumns = useCallback(() => [
    {
      title: intl.formatMessage({ id: "programme" }),
      dataIndex: 'programName',
      key: 'programName',
      render: (_, { programName }) => (
        <div className="nameContainer">
          <div className="blockWithText">{programName}</div>
        </div>
      ),
      onHeaderCell: (column) => ({ onClick: () => handleSort(column) }),
      sorter: () => { }
    },
    {
      title: intl.formatMessage({ id: "entity" }),
      dataIndex: 'charityName',
      key: 'charityName',
      onHeaderCell: (column) => ({ onClick: () => handleSort(column) }),
      sorter: () => { }
    },
    {
      title: intl.formatMessage({ id: "opportunity" }),
      dataIndex: 'opportunityName',
      key: 'opportunityName',
      onHeaderCell: (column) => ({ onClick: () => handleSort(column) }),
      sorter: () => { }
    },
    {
      title: intl.formatMessage({ id: "date_applied" }),
      dataIndex: 'updateDate',
      key: 'updateDate',
      onHeaderCell: (column) => ({ onClick: () => handleSort(column) }),
      sorter: () => { }
    },
    {
      title: intl.formatMessage({ id: "request" }),
      dataIndex: 'totalCashRequested',
      key: 'totalCashRequested',
      render: (text) => typeof text === 'string' && text != null && text.trim() ? (
        <span>{currencySymbol}{parseFloat(text.trim()).toFixed(2)}</span>
      ) : null,
      onHeaderCell: (column) => ({ onClick: () => handleSort(column) }),
      sorter: () => { }
    },
    {
      title: intl.formatMessage({ id: "offer" }),
      dataIndex: 'offer',
      key: 'offer',
      render: (text, record) => (
        offerMode && selectedRowKeys.includes(record.key) ? (
          <InputNumber
            min={0}
            value={offerValues[record.key] !== undefined ? offerValues[record.key] : (record.offer || record.totalCashRequested)}
            onChange={(value) => handleOfferChange(value, record)}
            addonBefore={currencySymbol}
          />
        ) : (
          typeof text === 'string' && text != null && text.trim() ? <span>{currencySymbol}{parseFloat(text.trim()).toFixed(2)}</span> : null
        )
      ),
      onHeaderCell: (column) => ({ onClick: () => handleSort(column) }),
      sorter: () => { }
    },
    {
      title: intl.formatMessage({ id: "status" }),
      key: 'status',
      dataIndex: 'status',
      render: (_, { status, prevStatus }) => (
        <Popover>
          <Tag type='regular'>{intl.formatMessage({ id: "pre_approved_caps" })?.toUpperCase()}</Tag>
          {prevStatus === "InEdit" && (
            <Popover content={intl.formatMessage({ id: "this_application_has_been_edited" })}>
              <Avatar src={pen} alt="edit icon" />
            </Popover>
          )}
        </Popover>
      )
    },
    {
      title: intl.formatMessage({ id: "my_vote" }),
      dataIndex: 'sumVoterVote',
      key: 'sumVoterVote',
      render: (_, { votes }) => <span>{votes?.sumVoterVote || 0}</span>
    },
    {
      title: intl.formatMessage({ id: "net_vote" }),
      dataIndex: 'updateDate',
      key: 'updateDate',
      render: (_, { votes }) => (
        votes?.sumVotes && votes?.voterCount ? (
          <>
            <span>{votes.sumVotes}</span>
            <span>({votes.voterCount})</span>
          </>
        ) : null
      )
    },
    {
      title: intl.formatMessage({ id: "actions" }),
      key: 'actions',
      dataIndex: 'status',
      render: (_, { programID, opportunityID }) => (
        <Space size="middle">
          <button
            className='button'
            onClick={() => navigate(`/company/applications/${programID}/${opportunityID}`)}
          >
            {intl.formatMessage({ id: "review" })}
          </button>
        </Space>
      )
    }
  ], [intl, offerMode, selectedRowKeys, offerValues, handleSort, handleOfferChange, navigate, currencySymbol]);

  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
    getCheckboxProps: () => ({ disabled: !offerMode })
  }), [selectedRowKeys, offerMode]);

  const remainingBudgetMinusRequests = useMemo(() => {
    return (parseFloat(remainingBudgetCurrentPeriod) || 0) - totalCashRequested;
  }, [remainingBudgetCurrentPeriod, totalCashRequested]);

  return (
    <div className={baseClassName}>
      <HelmetAsync><title>Applications Page</title></HelmetAsync>
      <div className='subContentContainer'>
        {savedProgramId && savedProgramId !== "All" && (
          <div className="budgetPlayGroundContainer">
            <div className="budgetPlayGroundSubContainer">
              <div className="switchItemContainer">
                <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "budget_period" })}</StyledText>
                <Select
                  className="roundDropdown"
                  allowClear
                  disabled={!roundOptions?.length}
                  options={roundOptions?.map((value, index) => ({
                    key: index.toString(),
                    label: value,
                    value: value
                  }))}
                  value={roundId}
                  placeholder={
                    roundOptions?.length
                      ? intl.formatMessage({ id: 'budget_period' })
                      : intl.formatMessage({ id: 'no_rounds_available' })
                  }
                  onChange={(value) => {
                    setRoundId(value);
                    sessionStorage.setItem('savedRoundId', value || '');
                    if (!value) {
                      form.setFieldsValue({
                        donationProgramID: undefined,
                        donationRound: undefined,
                        donationOpportunityID: undefined
                      });
                      setIsLoading(true);
                      setAllInteractions(null);
                      setFilteredInteractions(null);
                      setSelectedRowKeys([]);
                      setOfferValues({});
                      dispatch(getActionInteraction({
                        sortBy: "programName",
                        asc: "true",
                        filterBy: {
                          status: ["PreApproved"],
                          programID: savedProgramId,
                          companyID: decodeToken?.companyID
                        },
                        limit: "100"
                      }));
                    }
                  }}
                />
              </div>
              <div className="switchItemContainer">
                <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "offer_mode" })}</StyledText>
                <Switch className="switchItem" onChange={() => setOfferMode(prev => !prev)} />
              </div>
              {offerMode && <StyledText as="p" variant="B3">{intl.formatMessage({ id: "select_a_row_to_make_an_offer" })}</StyledText>}
            </div>
            <div className="budgetPlayGroundSubContainer">
              <div className="switchItemContainer">
                <StyledText as="p" variant="B3d">{`${intl.formatMessage({ id: "remaining_budget" })}:`}</StyledText>
                <StyledText as="p" variant="B3d">{`${currencySymbol}${remainingBudgetCurrentPeriod || 0}`}</StyledText>
              </div>
              <div className="switchItemContainer">
                <StyledText as="p" variant="B3d">{`${intl.formatMessage({ id: "total_requests" })}:`}</StyledText>
                <StyledText as="p" variant="B3d">{`${currencySymbol}${totalCashRequested.toFixed(2)}`}</StyledText>
              </div>
              <div className="switchItemContainer">
                <StyledText as="p" variant="B3d">{`${intl.formatMessage({ id: "remaining_budget_minus_requests" })}:`}</StyledText>
                <StyledText as="p" variant={remainingBudgetMinusRequests === 0 ? "B3d" : remainingBudgetMinusRequests > 0 ? "B3e" : "B3f"}>
                  {`${currencySymbol}${remainingBudgetMinusRequests.toFixed(2)}`}
                </StyledText>
              </div>
              {remainingBudgetWithOffersValue && offerMode && <div className="switchItemContainer">
                <StyledText as="p" variant="B3g">{`${intl.formatMessage({ id: "remaining_budget_with_offers" })}:`}</StyledText>
                <StyledText as="p" variant={parseFloat(remainingBudgetWithOffersValue) === 0 ? "B3d" : parseFloat(remainingBudgetWithOffersValue) > 0 ? "B3e" : "B3f"}>
                  {`${currencySymbol}${remainingBudgetWithOffersValue}`}
                </StyledText>
              </div>}
              {offerMode && (
                <div className="switchItemContainer">
                  <Button
                    variant="primary"
                    type="button"
                    disabled={!selectedRowKeys?.length}
                    onClick={handleSaveOffers}
                  >
                    {intl.formatMessage({ id: "save_offers" })}
                  </Button>
                  <Button
                    variant="primary"
                    type="button"
                    disabled={!selectedRowKeys?.length || selectedRowKeys.some(key => {
                      const offerValue = offerValues[key] !== undefined ? offerValues[key] : (filteredInteractions[key].offer || filteredInteractions[key].totalCashRequested);
                      return offerValue === undefined || offerValue === null || offerValue === '';
                    })}
                    onClick={() => setShowOfferConfirmationModal(true)}
                  >
                    {intl.formatMessage({ id: "send_selected_offers" })}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        <Table
          rowSelection={offerMode ? rowSelection : undefined}
          columns={getColumns()}
          loading={isLoading}
          dataSource={getTableData(filteredInteractions)}
          locale={{
            emptyText: (
              <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <StyledText variant="B3">{intl.formatMessage({ id: "no_data_found" })}</StyledText>
              </div>
            )
          }}
        />
      </div>
      <Modal
        className="offerConfirmation"
        title={intl.formatMessage({ id: 'send_offers_confirmation' })}
        open={showOfferConfirmationModal}
        onOk={() => {
          handleSendOffers();
          setShowOfferConfirmationModal(false);
        }}
        onCancel={() => setShowOfferConfirmationModal(false)}
        okText={intl.formatMessage({ id: 'send' })}
        showFooter
      >
        <Layout className='dropDownBackground'>
          <StyledText as="p" variant="B3">{intl.formatMessage({ id: "clicking_send_will_send_the_following_message_and_enable_editing_to_all_projects_that_you_have_selected_in_the_table" })}</StyledText>
          <StyledText as="p" variant="B3" className="italicFont">{intl.formatMessage({ id: "congratulations_we_would_like_to_approve_your_project" })}</StyledText>
        </Layout>
      </Modal>
    </div>
  );
};

const StyledPreApprovedAction = styled(PreApprovedAction)`
  &.applicationAction {
    background: ${(props) => props.theme.backgroundCanvas};
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    .nameContainer {
      display: flex;
      gap: 10px;
    }
    .switchItemContainer {
      display: flex;
      justify-content: flex-start;
      gap: 30px;
      .switchItem {
        width: max-contentstrat;
      }
    }
    .budgetPlayGroundContainer {
      display: flex;
      gap: 80px;
      background: white;
      border-radius: 10px;
      width: max-content;
      padding: 15px;
      margin-bottom: 15px;
      .budgetPlayGroundSubContainer {
        display: flex;
        flex-direction: column;
        gap: 15px;
      }
    }
    .roundDropdown {
      width: 250px;
    }
    .dropDownBackground {
      display: flex;
    }
    .button {
      border: 0;
      background: transparent;
      color: #5550F7;
      cursor: pointer;
      min-width: 100px;
    }
  }
`;

export default StyledPreApprovedAction;